import { useEffect, useState } from 'react'
import { type_CD_STATE, type_GM_STATE } from '../../helpers'
import { addCartItem, addMultipleCartItems } from '../helpers/cart'
import { globalEmitter } from '../helpers/emitter'
import { initOmneo } from '../helpers/omneo'
import { OmneoListItem, OmneoListItemMeta, PropsWishlistSharedDisplayState } from '../types'
import { LoaderSpin } from './LoaderSpin'
import { WishlistCard } from './WishlistCard'
import { DashboardError } from './DashboardError'
import { attachRequiredStorefrontInformationToOmneoItem, getStorefrontProductsBySkus, itemShouldBeHidden } from '../helpers/utils'
import { OmneoListItemInStore } from '../types/omneo-wishlist-item'

declare var CD_STATE: type_CD_STATE
declare var GM_STATE: type_GM_STATE

export function WishlistSharedDisplay() {
  const [state, setState] = useState<PropsWishlistSharedDisplayState>({
    loading: true,
    items: [],
    emptyWishlist: false,
    error: false,
    errorType: null,
    name: 'Wishlist'
  })
  const [currentItem, setCurrentItem] = useState<OmneoListItemMeta | null>(null)
  const { noHandle, network, invalidHandle, linkHref, linkTitle } = GM_STATE.wishlist.sharedWishlist.errorMessages

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    if (!searchParams.get('handle')) {
      setState({
        ...state,
        loading: false,
        error: true,
        errorType: 'noHandle'
      })
      return
    }

    const getWishlist = async () => {
      await initOmneo()
      const listId = searchParams.get('handle')
      const url = `${CD_STATE.omneo.rootUrl}/api/v1/lists/shares/${listId}`
      const config = {
        headers: {
          'content-type': 'application/json',
          Authorization: `Bearer ${window.omneoService._b_t}`
        }
      }
      fetch(url, config)
        .then((res) => {
          if (!res.ok || !res.json) {
            throw res
          }
          return res.json()
        })
        .then(async (body) => {
          if (!body?.data?.list) {
            throw body
          }

          const list = body.data.list

          let listItems: Array<OmneoListItem | OmneoListItemInStore> = (list.items || [])
            .filter((item: OmneoListItem | OmneoListItemInStore) => !itemShouldBeHidden(item))

          const skus = listItems.map((item) => item.product_variant.sku)
          const storefrontProducts = await getStorefrontProductsBySkus(skus)

          listItems = listItems.map(item => attachRequiredStorefrontInformationToOmneoItem(item, storefrontProducts, list.definition.type))

          const emptyWishlist = listItems.length ? false : true

          setState({
            ...state,
            loading: false,
            items: listItems,
            emptyWishlist,
            error: false
          })
        })
        .catch((err) => {
          console.error('Could not get wishlist by shared id', listId, url, err)
          const errorType = err?.status === 404 ? 'invalidHandle' : 'network'
          setState({
            ...state,
            loading: false,
            error: true,
            errorType
          })
        })
    }

    getWishlist()
  }, [])

  if (state.error) {
    let errorMessage = network
    switch (state.errorType) {
      case 'noHandle':
        errorMessage = noHandle
      case 'invalidHandle':
        errorMessage = invalidHandle
      case 'network':
        errorMessage = network
    }
    return <DashboardError errorMessage={errorMessage} linkHref={linkHref} linkTitle={linkTitle} />
  }

  if (state.loading && !currentItem) {
    return (
      <div className="relative w-100 p-4 md:p-32 flex flex-col justify-center items-center ">
        <LoaderSpin />
      </div>
    )
  }

  const addToBag = async (item: OmneoListItemMeta, index: number) => {
    setCurrentItem(item)
    setState({ ...state, loading: true, items: state.items, emptyWishlist: false, error: false })

    try {
      if (item.properties) {
        const properties = {
          items: item.properties.map((item: any) => {
            const newItem = { ...item };

            if (newItem.properties.hasOwnProperty('_uniqueTimeStamp')) {
              newItem.properties._uniqueTimeStamp = Date.now();
            }
            if (newItem.properties.hasOwnProperty('_uniqueItem')) {
              newItem.properties._uniqueItem = Date.now();
            }
            if (Array.isArray(newItem.properties) && newItem.properties.length === 0) {
              newItem.properties = {}
            }
    
            return newItem;
          })
        }

        const rest = await addMultipleCartItems(properties)
        if (rest) {
          globalEmitter.emit('product:added', { quantity: 1, variant: { name: rest.items.find((i: any) => i.id === item.variantId)?.title || rest.items[0]?.title } })
        }
      } else {
        const properties = {
          _uniqueTimeStamp: Date.now()
        }
        const res = await addCartItem(item.variantId, properties)
        if (res) {
          globalEmitter.emit('product:added', { quantity: 1, variant: { name: res.items[0]?.title } })
        }
      }
    } catch (e) {
      console.error('error', e)
      globalEmitter.emit('triggerNotification', { message: 'Add item to cart failed', type: 'error' })
    }
    setCurrentItem(null)
    setState({ ...state, loading: false, items: state.items, emptyWishlist: false, error: false })
  }

  return (
    <div className="grid grid-cols-2 gap-x-2.5 gap-y-6 md:gap-x-5 md:grid-cols-3 lg:grid-cols-4 md-max:-mx-3 md-max:-my-1 px-5">
      { (state.items.length === 0 || state.emptyWishlist) ?
        (
          <div className="w-full h-full flex flex-col justify-center items-center">No items to share</div>
        ):(
          state.items
            .filter((item: OmneoListItem | OmneoListItemInStore) => !item.meta.hidden)
            .map((item: OmneoListItem | OmneoListItemInStore, index) => (
              <WishlistCard
                key={item.id}
                item={item}
                addToBag={addToBag}
                loading={state.loading}
                currentVariantId={currentItem?.variantId || null}
                allowRemoveFromList={false}
                wishlistType='shared'
                index={index}
                currentIndex={null}
              />
            )))
        }
    </div>
  )
}
